import { useState } from "react";
import SectionHeading from "./SectionHeading";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da1fn30mlpfrg29fphfg";

const interestOptions = [
  "剧情讨论",
  "作品安利",
  "同人创作",
  "圈子活动",
  "社区资讯",
];

type Status = "idle" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾蜜罐检测
    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      // 机器人命中蜜罐：返回与正常提交一致的成功提示，不发送请求
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const email = String(fd.get("email") || "");
    if (!email) {
      setStatus("error");
      setFormError("请填写你的邮箱地址。");
      return;
    }

    const body = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      body.append(key, String(value));
    });

    setStatus("idle");
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.code ||
        responseText;
      const isSpam =
        serverMsg &&
        typeof serverMsg === "string" &&
        serverMsg.toLowerCase().includes("spam");

      if (!res.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后重试。");
        return;
      }
      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionHeading
              eyebrow="联系我们"
              title="订阅社区，第一时间接收精彩"
              align="left"
              desc="留下你的邮箱，即可订阅腐漫网的精选话题、推荐作品与圈子活动资讯。我们会用心筛选，只把值得的内容送到你身边。"
            />
            <div className="mt-8 flex flex-col gap-3">
              <div className="flex items-center gap-4 rounded-2xl border border-background-200 bg-background-100 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-800">
                  <i className="ri-mail-send-line text-xl"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">订阅邮箱</p>
                  <p className="text-sm text-foreground-500">hi@fumanhua.example</p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-background-200 bg-background-100 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
                  <i className="ri-message-2-line text-xl"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">社区反馈</p>
                  <p className="text-sm text-foreground-500">你的每一条建议，都会被认真对待</p>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form="腐漫社区订阅表单"
            className="flex flex-col gap-4 rounded-3xl border border-background-200 bg-background-100 p-6 md:p-8"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="nickname" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  怎么称呼你
                </label>
                <input
                  id="nickname"
                  name="nickname"
                  type="text"
                  placeholder="你的昵称"
                  autoComplete="nickname"
                  className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label htmlFor="interest" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  感兴趣的方向
                </label>
                <select
                  id="interest"
                  name="interest"
                  className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  {interestOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                邮箱地址 <span className="text-primary-600">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                autoComplete="email"
                className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>

            <div>
              <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                想对我们说点什么（选填）
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                maxLength={500}
                placeholder="写下你的想法或建议…"
                className="w-full resize-none rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="rdy-honeypot"
            />

            <button
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-send-plane-fill text-base"></i>
              订阅社区
            </button>

            {status === "success" && (
              <p className="flex items-center gap-2 rounded-xl bg-accent-100 px-4 py-3 text-sm text-accent-900">
                <i className="ri-checkbox-circle-fill text-base"></i>
                订阅成功，欢迎加入腐漫网大家庭！
              </p>
            )}
            {status === "error" && formError && (
              <p className="flex items-center gap-2 rounded-xl bg-primary-100 px-4 py-3 text-sm text-primary-800">
                <i className="ri-error-warning-fill text-base"></i>
                {formError}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}